import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  limit,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  writeBatch
} from 'firebase/firestore';
import { auth, db } from './firebase';
import { functionsClient } from './firebase';
import { httpsCallable } from 'firebase/functions';
import type { QueryDocumentSnapshot } from 'firebase/firestore';

export type OwnerReportAction = 'will_correct' | 'corrected' | 'keep_as_is';
export type AdminReportAction = 'under_review' | 'dismissed' | 'warning_sent' | 'content_removed' | 'user_suspended';

const blockedUsersRef = (uid: string) => collection(db, 'users', uid, 'blockedUsers');

export const getBlockedUserIds = async (uid: string): Promise<Set<string>> => {
  const snapshot = await getDocs(query(blockedUsersRef(uid), limit(500)));
  return new Set(snapshot.docs.map(item => item.id));
};

export const subscribeToBlockedUsers = (
  uid: string,
  callback: (blockedUserIds: Set<string>) => void,
  onError?: (error: Error) => void
): (() => void) => onSnapshot(
  query(blockedUsersRef(uid), limit(500)),
  snapshot => callback(new Set(snapshot.docs.map(item => item.id))),
  error => {
    console.error('Could not load blocked users:', error);
    onError?.(error);
  }
);

export const isBlockedEitherWay = async (uid: string, otherUid: string): Promise<boolean> => {
  if (!uid || !otherUid || uid === otherUid) return false;
  const [outgoing, incoming] = await Promise.all([
    getDoc(doc(db, 'users', uid, 'blockedUsers', otherUid)),
    getDoc(doc(db, 'users', otherUid, 'blockedUsers', uid))
  ]);
  return outgoing.exists() || incoming.exists();
};

export const blockUser = async (uid: string, blockedUid: string): Promise<void> => {
  if (!auth.currentUser || auth.currentUser.uid !== uid) {
    throw new Error('Sign in to block this account.');
  }
  if (!blockedUid || uid === blockedUid) {
    throw new Error('You cannot block this account.');
  }
  await setDoc(doc(db, 'users', uid, 'blockedUsers', blockedUid), {
    blockedUid,
    createdAt: serverTimestamp()
  });
};

export const unblockUser = async (uid: string, blockedUid: string): Promise<void> => {
  if (!auth.currentUser || auth.currentUser.uid !== uid) {
    throw new Error('Sign in to manage blocked accounts.');
  }
  await deleteDoc(doc(db, 'users', uid, 'blockedUsers', blockedUid));
};

export const reportContent = async (
  reporterUid: string,
  contentId: string,
  contentType: 'photo' | 'short' | 'long',
  contentOwnerUid: string,
  reason: string,
  details: string,
  contentTitle = '',
  contentThumbUrl = ''
): Promise<void> => {
  if (!auth.currentUser || auth.currentUser.uid !== reporterUid) {
    throw new Error('Sign in to report this content.');
  }
  if (!contentId || !contentOwnerUid || reporterUid === contentOwnerUid) {
    throw new Error('This content cannot be reported.');
  }
  if (reason.trim().length === 0 || reason.trim().length > 100) {
    throw new Error('Choose a valid report reason.');
  }
  if (details.trim().length > 1000 || contentTitle.length > 200 || contentThumbUrl.length > 2048) {
    throw new Error('The report contains text that is too long.');
  }
  const reportRef = doc(collection(db, 'content_reports'));
  const ownerReportRef = doc(db, 'content_reports_received', contentOwnerUid, 'reports', reportRef.id);
  const reportedAt = serverTimestamp();
  const report = {
    reporterUid,
    contentId,
    contentType,
    contentOwnerUid,
    reason: reason.trim(),
    details: details.trim(),
    contentTitle: contentTitle.slice(0, 200),
    contentThumbUrl: contentThumbUrl.slice(0, 2048),
    reportedAt,
    status: 'open'
  };
  const batch = writeBatch(db);
  batch.set(reportRef, report);
  batch.set(ownerReportRef, {
    reportId: reportRef.id,
    contentId,
    contentType,
    contentOwnerUid,
    reason: report.reason,
    details: report.details,
    contentTitle: report.contentTitle,
    contentThumbUrl: report.contentThumbUrl,
    reportedAt,
    status: 'open'
  });
  await batch.commit();
};

export interface ContentReport {
  id: string;
  reporterUid?: string;
  contentId: string;
  contentOwnerUid: string;
  contentType: 'photo' | 'short' | 'long';
  reason: string;
  details: string;
  contentTitle?: string;
  contentThumbUrl?: string;
  status: 'open' | 'under_review' | 'actioned' | 'dismissed';
  reportedAt?: unknown;
}

export interface ReportEvent {
  id: string;
  reportId: string;
  contentOwnerUid: string;
  contentTitle?: string;
  type: 'owner_response' | 'admin_action';
  actorUid: string;
  actorRole: string;
  action: OwnerReportAction | AdminReportAction;
  note: string;
  createdAt?: unknown;
}

export const respondToContentReport = async (
  report: ContentReport,
  action: OwnerReportAction,
  note: string
): Promise<void> => {
  const user = auth.currentUser;
  const cleanNote = note.trim();
  if (!user || user.uid !== report.contentOwnerUid) {
    throw new Error('Only the content owner can respond to this report.');
  }
  if (cleanNote.length < 10 || cleanNote.length > 1000) {
    throw new Error('Your response must be between 10 and 1000 characters.');
  }
  const token = await user.getIdTokenResult();
  const role = String(token.claims.role || 'user');
  await addDoc(collection(db, 'content_reports', report.id, 'events'), {
    type: 'owner_response',
    actorUid: user.uid,
    actorRole: role,
    action,
    note: cleanNote,
    reportId: report.id,
    contentOwnerUid: report.contentOwnerUid,
    contentTitle: report.contentTitle || '',
    createdAt: serverTimestamp()
  });
};

export const recordAdminReportAction = async (
  reportId: string,
  action: AdminReportAction,
  note: string
): Promise<void> => {
  const user = auth.currentUser;
  const cleanNote = note.trim();
  if (!user) throw new Error('Sign in with an administrator account.');
  if (cleanNote.length < 3 || cleanNote.length > 1000) {
    throw new Error('A reason between 3 and 1000 characters is required.');
  }
  await httpsCallable(functionsClient, 'moderateReport')({
    reportId,
    action,
    reason: cleanNote
  });
};

export const loadReportEvents = async (
  reportId: string,
  pageSize = 20
): Promise<{ events: ReportEvent[]; cursor: QueryDocumentSnapshot | null; hasMore: boolean }> => {
  const snapshot = await getDocs(query(
    collection(db, 'content_reports', reportId, 'events'),
    orderBy('createdAt', 'desc'),
    limit(pageSize + 1)
  ));
  const page = snapshot.docs.slice(0, pageSize);
  return {
    events: page.map(event => ({ id: event.id, ...event.data() } as ReportEvent)),
    cursor: page.at(-1) || null,
    hasMore: snapshot.docs.length > pageSize
  };
};
